import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';

const STATUS_COLORS = {
  New: 'bg-gray-500/20 text-gray-400',
  Emailed: 'bg-blue-500/20 text-blue-400',
  'Follow Up': 'bg-orange-500/20 text-orange-400',
  Replied: 'bg-purple-500/20 text-purple-400',
  Booked: 'bg-green-500/20 text-green-400',
  Closed: 'bg-emerald-700/20 text-emerald-400',
  Cold: 'bg-gray-400/10 text-gray-500',
};

export default function EmailQueue() {
  const [prospects, setProspects] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [draftEmail, setDraftEmail] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('compose');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendingAll, setSendingAll] = useState(false);

  const load = useCallback(() => {
    api.getProspects().then(data => setProspects(Array.isArray(data) ? data : [])).catch(e => setError(e.message));
    api.getEmailLog().then(data => setEmailLog(Array.isArray(data) ? data : [])).catch(() => {});
    api.checkFollowUps().then(data => setFollowUps(Array.isArray(data?.pending) ? data.pending : [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async (prospectId, isFollowUp = false) => {
    setGenerating(prospectId);
    setError('');
    setSuccess('');
    try {
      const result = await api.generateEmail(prospectId, isFollowUp);
      setDraftEmail({ ...result, isFollowUp });
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(null);
    }
  };

  const handleSend = async () => {
    if (!draftEmail) return;
    setSending(true);
    setError('');
    try {
      const result = await api.sendEmail(draftEmail.prospectId, draftEmail.subject, draftEmail.body);
      setSuccess(`Email sent! (${result.sentToday}/${result.dailyCap} today)`);
      setDraftEmail(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendAll = async () => {
    const newWithEmail = prospects.filter(p => p.email && p.status === 'New');
    if (newWithEmail.length === 0) return alert('No new prospects with emails to send to.');
    if (!window.confirm(`This will AI-generate and send personalised emails to ${newWithEmail.length} prospects. Continue?`)) return;
    setSendingAll(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.sendAllEmails();
      setSuccess(`Sent ${result.sent}/${result.total} emails!${result.errors?.length ? ` Errors: ${result.errors.join(', ')}` : ''}`);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSendingAll(false);
    }
  };

  const readyToEmail = prospects.filter(p =>
    p.email && (p.status === 'New' || p.status === 'Follow Up')
  );

  const today = new Date().toISOString().split('T')[0];
  const sentToday = emailLog.filter(e => e.sentAt && e.sentAt.startsWith(today)).length;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Email Queue</h2>

      {/* Daily cap warning */}
      {sentToday >= 40 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4 text-sm text-orange-400">
          Warning: {sentToday} emails sent today. Approaching daily cap of 50.
        </div>
      )}

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-sm text-red-400">{error}</div>}
      {success && <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4 text-sm text-green-400">{success}</div>}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-900 rounded-lg p-1 w-fit">
        {[
          { key: 'compose', label: `Compose (${readyToEmail.length})` },
          { key: 'followups', label: `Follow Ups (${followUps.length})` },
          { key: 'log', label: `Sent Log (${emailLog.length})` }
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Send All Button */}
      {readyToEmail.filter(p => p.status === 'New').length > 0 && (
        <button
          onClick={handleSendAll}
          disabled={sendingAll}
          className="mb-6 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
        >
          {sendingAll ? 'Generating & Sending... (this takes a while)' : `Send Emails to All ${readyToEmail.filter(p => p.status === 'New').length} New Prospects`}
        </button>
      )}

      {/* Draft Email Preview */}
      {draftEmail && (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 mb-6">
          <h3 className="text-lg font-semibold mb-3">
            {draftEmail.isFollowUp ? 'Follow-Up Draft' : 'Email Draft'}
          </h3>
          <div className="mb-3">
            <label className="block text-xs text-gray-400 mb-1">Subject</label>
            <input
              type="text"
              value={draftEmail.subject}
              onChange={e => setDraftEmail({ ...draftEmail, subject: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-1">Body</label>
            <textarea
              value={draftEmail.body}
              onChange={e => setDraftEmail({ ...draftEmail, body: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 min-h-[200px]"
              rows={10}
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSend}
              disabled={sending}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {sending ? 'Sending...' : 'Approve & Send'}
            </button>
            <button
              onClick={() => handleGenerate(draftEmail.prospectId, draftEmail.isFollowUp)}
              disabled={generating}
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              Regenerate
            </button>
            <button
              onClick={() => setDraftEmail(null)}
              className="text-gray-400 hover:text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Compose Tab */}
      {tab === 'compose' && (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900">
              <tr className="text-left text-gray-400">
                <th className="px-4 py-3 font-medium">Business</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Service Gap</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {readyToEmail.map(p => (
                <tr key={p.id} className="hover:bg-gray-900/50">
                  <td className="px-4 py-3 font-medium">{p.businessName}</td>
                  <td className="px-4 py-3 text-gray-400">{p.ownerName || '—'}</td>
                  <td className="px-4 py-3 text-gray-400">{p.email}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-400">{p.serviceGap}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[p.status]}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleGenerate(p.id, false)}
                      disabled={generating === p.id}
                      className="text-xs px-4 py-1.5 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                    >
                      {generating === p.id ? 'Writing...' : 'Write Email'}
                    </button>
                  </td>
                </tr>
              ))}
              {readyToEmail.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No prospects ready to email. Add email addresses in the Prospects tab.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Follow Ups Tab */}
      {tab === 'followups' && (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900">
              <tr className="text-left text-gray-400">
                <th className="px-4 py-3 font-medium">Business</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Last Emailed</th>
                <th className="px-4 py-3 font-medium">Follow Ups Sent</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {followUps.map(p => (
                <tr key={p.id} className="hover:bg-gray-900/50">
                  <td className="px-4 py-3 font-medium">{p.businessName}</td>
                  <td className="px-4 py-3 text-gray-400">{p.email}</td>
                  <td className="px-4 py-3 text-gray-400">
                    {p.lastEmailedAt ? new Date(p.lastEmailedAt).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{p.followUpCount || 0}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleGenerate(p.id, true)}
                      disabled={generating === p.id}
                      className="text-xs px-4 py-1.5 bg-orange-600/20 text-orange-400 rounded-lg hover:bg-orange-600/30 transition-colors disabled:opacity-50"
                    >
                      {generating === p.id ? 'Writing...' : 'Write Follow Up'}
                    </button>
                  </td>
                </tr>
              ))}
              {followUps.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No follow ups pending.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Sent Log Tab */}
      {tab === 'log' && (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900">
              <tr className="text-left text-gray-400">
                <th className="px-4 py-3 font-medium">Business</th>
                <th className="px-4 py-3 font-medium">To</th>
                <th className="px-4 py-3 font-medium">Subject</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Sent At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {[...emailLog].reverse().map((e, i) => (
                <tr key={i} className="hover:bg-gray-900/50">
                  <td className="px-4 py-3 font-medium">{e.businessName}</td>
                  <td className="px-4 py-3 text-gray-400">{e.to}</td>
                  <td className="px-4 py-3 text-gray-400 max-w-[200px] truncate">{e.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${e.type === 'follow-up' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {e.type === 'follow-up' ? 'Follow Up' : 'Initial'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {new Date(e.sentAt).toLocaleString('en-AU')}
                  </td>
                </tr>
              ))}
              {emailLog.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No emails sent yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
